// ==========================================
// FILE: frontend/src/components/SuperAdminApproval.jsx
// ==========================================
import React, { useEffect, useState } from "react";
import { Loader, CheckCircle, XCircle, Mail, User, ShieldCheck, AlertCircle } from "lucide-react";
import { apiService } from '../../services/api.service'; 
import { ROUTES, STORAGE_KEYS } from '../../config/api.config'; 
import Navigation from "../Navigation"; 

export default function SuperAdminApproval() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(""); 
  const [message, setMessage] = useState({ type: "", text: "" });

  // 1. SECURITY: Auth Guard & Role Verification
  useEffect(() => {
    const checkAccess = () => {
      if (!apiService.isAuthenticated()) {
        window.location.href = ROUTES.SUPER_ADMIN_LOGIN || '/super-admin/login';
        return;
      }

      const userDataString = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      let userData = {};
      try {
        userData = JSON.parse(userDataString);
      } catch (e) {
        apiService.logout();
        return;
      }

      if (userData?.role !== 'super_admin') {
        window.location.href = ROUTES.ADMIN_DASHBOARD || '/admin/dashboard'; 
        return;
      }
      
      fetchRequests();
    };

    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/admin/requests/pending');
      if (response.success && Array.isArray(response.data)) {
        setRequests(response.data);
      } else {
        setRequests([]);
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load pending requests." });
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (email, action) => {
    setActionLoading(email);
    setMessage({ type: "", text: "" });

    try {
      const endpoint = `/admin/requests/${email}/${action}`;
      const response = await apiService.post(endpoint, {});

      if (response.success) {
        setMessage({ 
          type: "success", 
          text: `Request ${action}ed successfully!` 
        });
        fetchRequests();
      } else {
        throw new Error(response.message || `Failed to ${action}`);
      }
    } catch (err) {
      setMessage({ 
        type: "error", 
        text: err.message || "Server connection failed" 
      });
    } finally {
      setActionLoading("");
    }
  };

  return (
    // 👇 CHANGED pt-24 TO pt-40 (Increased top padding to push content down)
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 pt-40 px-6 pb-12 relative">
      
      {/* Navigation sits on top (Fixed) */}
      <Navigation />

      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-purple-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] right-[5%] w-80 h-80 bg-blue-400/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto bg-white/80 backdrop-blur-xl shadow-xl rounded-3xl p-8 border border-white/50 relative z-10">
        
        {/* Header */}
        <div className="flex items-center gap-5 mb-8 border-b border-gray-100 pb-6">
          <div className="p-4 bg-purple-600 text-white rounded-2xl shadow-lg shadow-purple-200">
            <ShieldCheck size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Pending Approvals</h1>
            <p className="text-gray-500 text-sm mt-1">Super Admin Control Panel</p>
          </div>
        </div>

        {/* Feedback Messages */}
        {message.text && (
          <div className={`p-4 rounded-xl mb-6 flex items-center gap-3 border ${
            message.type === 'error' 
              ? 'bg-red-50 border-red-200 text-red-700' 
              : 'bg-green-50 border-green-200 text-green-700'
          }`}>
            {message.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* Content Area */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Loader className="animate-spin text-purple-600 mb-3" size={40} />
            <p>Verifying credentials & fetching requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200">
             <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-gray-400" size={32} />
             </div>
             <p className="text-gray-500 font-medium text-lg">All caught up!</p>
             <p className="text-gray-400 text-sm">No pending admin requests found.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {requests.map((req) => (
              <div 
                key={req.email} 
                className="group p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-purple-100 transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  
                  {/* User Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                        <User size={20} />
                      </div>
                      <h3 className="font-bold text-lg text-gray-800">{req.full_name}</h3>
                    </div>
                    <div className="pl-[52px]">
                      <p className="text-gray-500 flex items-center gap-2 text-sm font-medium">
                        <Mail size={16} className="text-gray-400" /> {req.email}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">Requested: {new Date(req.created_at || Date.now()).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-3 w-full md:w-auto mt-4 md:mt-0">
                    <button
                      onClick={() => handleAction(req.email, 'approve')}
                      disabled={!!actionLoading}
                      className="flex-1 md:flex-none h-12 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center justify-center gap-2 font-semibold shadow-sm hover:shadow-green-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === req.email ? (
                        <Loader className="animate-spin" size={18} />
                      ) : (
                        <>
                          <CheckCircle size={18} /> 
                          <span>Approve</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleAction(req.email, 'reject')}
                      disabled={!!actionLoading}
                      className="flex-1 md:flex-none h-12 px-6 bg-white hover:bg-red-50 text-gray-600 hover:text-red-600 border border-gray-200 hover:border-red-200 rounded-xl flex items-center justify-center gap-2 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle size={18} />
                      <span>Reject</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}